/**
 * Cotizaciones y facturas de CAUCARED: lista, editor y vista. Son la misma pieza porque son
 * el mismo documento en dos momentos — la cotización aceptada se convierte en factura sin
 * volver a escribirla.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { CAU, est, fmt, fecha, hoyISO, sumarDias, calcular, ESTADOS_COT, ESTADOS_PAGO, Pastilla,
  imprimir, abrirWhatsapp, mensajeDeError, costoSinIva } from './cauComun';

const URL = { cotizacion: '/api/caucared/cotizaciones', factura: '/api/caucared/facturas' };
const NOMBRE = { cotizacion: 'cotización', factura: 'factura' };

let llave = 0;
const nuevaLlave = () => ++llave;

// ─── LISTA ───────────────────────────────────────────────────

export default function CauDocumentos({ tipo, cfg, verId, qInicial, onVerFactura }) {
  const [vista, setVista] = useState(verId ? 'ver' : 'lista');
  const [lista, setLista] = useState([]);
  const [q, setQ] = useState(qInicial || '');
  const [estado, setEstado] = useState('');
  const [actual, setActual] = useState(null);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get(URL[tipo], { params: { q, estado: tipo === 'cotizacion' ? estado : '' } });
      setLista(data); setError('');
    } catch (e) { setError(mensajeDeError(e, 'No se pudo cargar la lista')); }
    finally { setCargando(false); }
  }, [tipo, q, estado]);

  useEffect(() => { const t = setTimeout(cargar, 250); return () => clearTimeout(t); }, [cargar]);

  const abrir = useCallback(async (id) => {
    try {
      const { data } = await api.get(`${URL[tipo]}/${id}`);
      setActual(data); setVista('ver');
    } catch (e) { setError(mensajeDeError(e, 'No se pudo abrir')); setVista('lista'); }
  }, [tipo]);

  useEffect(() => { if (verId) abrir(verId); }, [verId, abrir]);

  if (vista === 'editar') {
    return <Editor tipo={tipo} cfg={cfg} inicial={actual}
      onCancelar={() => setVista(actual ? 'ver' : 'lista')}
      onGuardado={(doc) => { abrir(doc.id); cargar(); }} />;
  }
  if (vista === 'ver' && actual) {
    return <Ver tipo={tipo} cfg={cfg} doc={actual}
      onVolver={() => { setVista('lista'); setActual(null); cargar(); }}
      onEditar={() => setVista('editar')}
      onCambio={(doc) => doc ? setActual(doc) : abrir(actual.id)}
      onBorrado={() => { setVista('lista'); setActual(null); cargar(); }}
      onVerFactura={onVerFactura} />;
  }

  const esCot = tipo === 'cotizacion';
  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
        <input style={{ ...est.input, flex: '1 1 220px', width: 'auto' }} placeholder={`Buscar por cliente o número…`}
          value={q} onChange={(e) => setQ(e.target.value)} />
        {esCot && (
          <select style={{ ...est.input, flex: '0 1 170px', width: 'auto' }} value={estado} onChange={(e) => setEstado(e.target.value)}>
            <option value="">Todos los estados</option>
            {Object.entries(ESTADOS_COT).map(([k, v]) => <option key={k} value={k}>{v.txt}</option>)}
          </select>
        )}
        <button style={est.boton} onClick={() => { setActual(null); setVista('editar'); }}>
          + Nueva {NOMBRE[tipo]}
        </button>
      </div>
      {error && <div style={{ ...est.error, marginBottom: '12px' }}>{error}</div>}
      {!cargando && !lista.length && !error && (
        <div style={{ ...est.tarjeta, textAlign: 'center', color: CAU.tenue, padding: '32px' }}>
          {q || estado ? 'No hay resultados con ese filtro.' : `Todavía no hay ${esCot ? 'cotizaciones' : 'facturas'} de CAUCARED.`}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {lista.map((d) => {
          const saldo = Number(d.neto) - Number(d.pagado);
          return (
            <div key={d.id} onClick={() => abrir(d.id)}
              style={{ ...est.tarjeta, padding: '12px 14px', cursor: 'pointer', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ minWidth: '92px' }}>
                <div style={{ fontWeight: 800, color: CAU.acento }}>{d.numero}</div>
                <div style={{ fontSize: '12px', color: CAU.tenue }}>{fecha(d.fecha)}</div>
              </div>
              <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{d.cliente_nombre}</div>
                <div style={{ fontSize: '12px', color: CAU.tenue }}>
                  {esCot
                    ? (d.factura_numero ? `Facturada en ${d.factura_numero}` : `Válida ${d.validez_dias} días`)
                    : [d.cotizacion_numero && `De ${d.cotizacion_numero}`, d.numero_fe && `FE ${d.numero_fe}`].filter(Boolean).join(' · ') || (d.forma_pago === 'contado' ? 'Contado' : `Vence ${fecha(d.fecha_vencimiento)}`)}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 800 }}>{fmt(esCot ? d.total : d.neto)}</div>
                {!esCot && d.estado !== 'anulada' && saldo > 0 && Number(d.pagado) > 0 && (
                  <div style={{ fontSize: '12px', color: CAU.acento }}>Debe {fmt(saldo)}</div>
                )}
              </div>
              <Pastilla info={esCot ? ESTADOS_COT[d.estado] : ESTADOS_PAGO[d.estado_pago]} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── EDITOR ──────────────────────────────────────────────────

function Editor({ tipo, cfg, inicial, onCancelar, onGuardado }) {
  const esFactura = tipo === 'factura';
  const responsableIva = cfg.responsable_iva !== false;
  const ivaDefecto = responsableIva ? Number(cfg.iva_pct ?? 19) : 0;

  const [clientes, setClientes] = useState([]);
  const [clienteId, setClienteId] = useState(inicial?.cliente_id || '');
  const [ocasional, setOcasional] = useState({
    cliente_nombre: inicial && !inicial.cliente_id ? inicial.cliente_nombre : '',
    cliente_documento: inicial && !inicial.cliente_id ? (inicial.cliente_documento || '') : '',
    cliente_telefono: inicial && !inicial.cliente_id ? (inicial.cliente_telefono || '') : '',
    cliente_email: inicial && !inicial.cliente_id ? (inicial.cliente_email || '') : '',
  });
  const [items, setItems] = useState(() => (inicial?.items || []).map((it) => ({
    key: nuevaLlave(), catalogo_producto_id: it.catalogo_producto_id, sku: it.sku, descripcion: it.descripcion,
    cantidad: Number(it.cantidad), precio_unitario: Number(it.precio_unitario), descuento_pct: Number(it.descuento_pct),
    iva_pct: Number(it.iva_pct), costo: it.costo_unitario == null ? null : Number(it.costo_unitario),
    costo_manual: !!it.costo_manual,
  })));
  const [f, setF] = useState({
    fecha: inicial?.fecha ? String(inicial.fecha).slice(0, 10) : hoyISO(),
    validez_dias: inicial?.validez_dias ?? cfg.validez_dias ?? 15,
    condiciones: inicial ? (inicial.condiciones || '') : (cfg.condiciones || ''),
    notas: inicial?.notas || '',
    forma_pago: inicial?.forma_pago || 'credito',
    plazo_dias: cfg.plazo_dias ?? 30,
    fecha_vencimiento: inicial?.fecha_vencimiento ? String(inicial.fecha_vencimiento).slice(0, 10) : '',
    numero_fe: inicial?.numero_fe || '',
    aplica_retefuente: !!inicial?.aplica_retefuente, retefuente_pct: inicial?.retefuente_pct ?? 2.5,
    aplica_reteica: !!inicial?.aplica_reteica, reteica_pct: inicial?.reteica_pct ?? 0,
    aplica_reteiva: !!inicial?.aplica_reteiva,
  });
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    api.get('/api/caucared/clientes').then(({ data }) => setClientes(data.filter((c) => c.activo || c.id === inicial?.cliente_id)))
      .catch((e) => setError(mensajeDeError(e, 'No se pudieron cargar los clientes')));
  }, [inicial]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // Al escoger cliente, sus retenciones pasan a la factura (se pueden cambiar aquí mismo)
  const escogerCliente = (id) => {
    setClienteId(id);
    const c = clientes.find((x) => x.id === id);
    if (c && esFactura) {
      setF((p) => ({ ...p,
        aplica_retefuente: !!c.aplica_retefuente, retefuente_pct: Number(c.retefuente_pct || 0),
        aplica_reteica: !!c.aplica_reteica, reteica_pct: Number(c.reteica_pct || 0),
        aplica_reteiva: !!c.aplica_reteiva }));
    }
  };

  const agregarProducto = (p) => setItems((prev) => [...prev, {
    key: nuevaLlave(), catalogo_producto_id: p.id, sku: p.sku, descripcion: p.nombre, cantidad: 1,
    precio_unitario: p.precio_sugerido, descuento_pct: 0, iva_pct: ivaDefecto, costo: p.costo, origen: p.origen_precio,
  }]);
  const agregarLibre = () => setItems((prev) => [...prev, {
    key: nuevaLlave(), catalogo_producto_id: null, descripcion: '', cantidad: 1, precio_unitario: 0,
    descuento_pct: 0, iva_pct: ivaDefecto, costo: null,
  }]);
  const cambiar = (key, k, v) => setItems((prev) => prev.map((it) => (it.key === key ? { ...it, [k]: v } : it)));
  const quitar = (key) => setItems((prev) => prev.filter((it) => it.key !== key));
  const mover = (key, d) => setItems((prev) => {
    const i = prev.findIndex((x) => x.key === key); const j = i + d;
    if (j < 0 || j >= prev.length) return prev;
    const c = [...prev]; [c[i], c[j]] = [c[j], c[i]]; return c;
  });

  const ret = esFactura ? f : {};
  const t = calcular(items, ret, responsableIva);

  const guardar = async () => {
    setError('');
    if (!clienteId && !ocasional.cliente_nombre.trim()) { setError('Escoge un cliente o escribe a nombre de quién va.'); return; }
    if (!items.some((it) => String(it.descripcion).trim())) { setError('Agrega al menos un renglón.'); return; }
    setGuardando(true);
    const cuerpo = {
      ...(clienteId ? { cliente_id: clienteId } : ocasional),
      fecha: f.fecha, notas: f.notas,
      items: items.map((it) => ({
        catalogo_producto_id: it.catalogo_producto_id || null, descripcion: it.descripcion,
        cantidad: it.cantidad, precio_unitario: it.precio_unitario, descuento_pct: it.descuento_pct, iva_pct: it.iva_pct,
        // Del catálogo el servidor relee el costo, salvo que se haya corregido a mano
        costo_unitario: it.catalogo_producto_id && !it.costo_manual ? undefined : (it.costo === '' ? null : it.costo),
        costo_manual: !!it.costo_manual,
      })),
      ...(esFactura
        ? { forma_pago: f.forma_pago, plazo_dias: f.plazo_dias, fecha_vencimiento: f.fecha_vencimiento || undefined,
            numero_fe: f.numero_fe, aplica_retefuente: f.aplica_retefuente, retefuente_pct: f.retefuente_pct,
            aplica_reteica: f.aplica_reteica, reteica_pct: f.reteica_pct, aplica_reteiva: f.aplica_reteiva }
        : { validez_dias: f.validez_dias, condiciones: f.condiciones }),
    };
    try {
      const { data } = inicial
        ? await api.put(`${URL[tipo]}/${inicial.id}`, cuerpo)
        : await api.post(URL[tipo], cuerpo);
      onGuardado(data);
    } catch (e) { setError(mensajeDeError(e, 'No se pudo guardar')); }
    finally { setGuardando(false); }
  };

  const clienteSel = clientes.find((c) => c.id === clienteId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <button style={est.botonSec} onClick={onCancelar}>← Volver</button>
        <h2 style={{ margin: 0, fontSize: '18px' }}>
          {inicial ? `Corregir ${NOMBRE[tipo]} ${inicial.numero}` : `Nueva ${NOMBRE[tipo]}`}
        </h2>
      </div>

      {/* Cliente */}
      <div style={est.tarjeta}>
        <label style={est.label}>Cliente</label>
        <select style={est.input} value={clienteId} onChange={(e) => escogerCliente(e.target.value)}>
          <option value="">— Cliente ocasional (escribir los datos) —</option>
          {clientes.map((c) => <option key={c.id} value={c.id}>{c.razon_social}{c.documento ? ` · ${c.documento}${c.dv != null && c.tipo_doc === 'NIT' ? '-' + c.dv : ''}` : ''}</option>)}
        </select>
        {!clienteId && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,200px),1fr))', gap: '10px', marginTop: '10px' }}>
            <div><label style={est.label}>Nombre o razón social *</label>
              <input style={est.input} value={ocasional.cliente_nombre} onChange={(e) => setOcasional({ ...ocasional, cliente_nombre: e.target.value })} /></div>
            <div><label style={est.label}>NIT o cédula</label>
              <input style={est.input} value={ocasional.cliente_documento} onChange={(e) => setOcasional({ ...ocasional, cliente_documento: e.target.value })} /></div>
            <div><label style={est.label}>Teléfono</label>
              <input style={est.input} value={ocasional.cliente_telefono} onChange={(e) => setOcasional({ ...ocasional, cliente_telefono: e.target.value })} /></div>
            <div><label style={est.label}>Correo</label>
              <input style={est.input} value={ocasional.cliente_email} onChange={(e) => setOcasional({ ...ocasional, cliente_email: e.target.value })} /></div>
          </div>
        )}
        {clienteSel && (
          <div style={{ fontSize: '12px', color: CAU.tenue, marginTop: '8px' }}>
            {[clienteSel.contacto, clienteSel.telefono, clienteSel.email, clienteSel.ciudad].filter(Boolean).join(' · ') || 'Sin datos de contacto'}
          </div>
        )}
      </div>

      {/* Renglones */}
      <div style={est.tarjeta}>
        <BuscarProducto onEscoger={agregarProducto} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
          {items.map((it, i) => (
            <Renglon key={it.key} it={it} n={i + 1} calc={t.renglones[i]} responsableIva={responsableIva}
              onCambiar={(k, v) => cambiar(it.key, k, v)} onQuitar={() => quitar(it.key)}
              onSubir={() => mover(it.key, -1)} onBajar={() => mover(it.key, 1)} />
          ))}
        </div>
        <button style={{ ...est.botonSec, marginTop: '12px', whiteSpace: 'normal', textAlign: 'left', maxWidth: '100%' }} onClick={agregarLibre}>+ Renglón libre (servicio, instalación, mano de obra…)</button>
      </div>

      {/* Datos del documento */}
      <div style={{ ...est.tarjeta, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,180px),1fr))', gap: '12px' }}>
        <div><label style={est.label}>Fecha</label>
          <input type="date" style={est.input} value={f.fecha} onChange={(e) => set('fecha', e.target.value)} /></div>
        {!esFactura && (
          <div><label style={est.label}>Validez (días)</label>
            <input type="number" min="1" style={est.input} value={f.validez_dias} onChange={(e) => set('validez_dias', e.target.value)} />
            <div style={{ fontSize: '11px', color: CAU.tenue, marginTop: '3px' }}>Hasta el {fecha(sumarDias(f.fecha, f.validez_dias))}</div></div>
        )}
        {esFactura && (<>
          <div><label style={est.label}>Forma de pago</label>
            <select style={est.input} value={f.forma_pago} onChange={(e) => set('forma_pago', e.target.value)}>
              <option value="credito">Crédito</option><option value="contado">Contado</option>
            </select></div>
          {f.forma_pago === 'credito' && (
            <div><label style={est.label}>Plazo (días)</label>
              <input type="number" min="0" style={est.input} value={f.plazo_dias}
                onChange={(e) => { set('plazo_dias', e.target.value); set('fecha_vencimiento', ''); }} />
              <div style={{ fontSize: '11px', color: CAU.tenue, marginTop: '3px' }}>
                Vence el {fecha(f.fecha_vencimiento || sumarDias(f.fecha, f.plazo_dias))}</div></div>
          )}
          <div><label style={est.label}>N.º de factura electrónica (si la emitiste aparte)</label>
            <input style={est.input} value={f.numero_fe} placeholder="Opcional" onChange={(e) => set('numero_fe', e.target.value)} /></div>
        </>)}
      </div>

      {esFactura && (
        <div style={est.tarjeta}>
          <div style={{ fontWeight: 700, marginBottom: '4px' }}>Retenciones que practica el cliente</div>
          <div style={{ fontSize: '12px', color: CAU.tenue, marginBottom: '10px' }}>
            Las descuenta la empresa al pagarte. Salen de la ficha del cliente; aquí puedes cambiarlas solo para esta factura.
          </div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <Retencion txt="En la fuente" activa={f.aplica_retefuente} pct={f.retefuente_pct}
              onActiva={(v) => set('aplica_retefuente', v)} onPct={(v) => set('retefuente_pct', v)} />
            <Retencion txt="De ICA" activa={f.aplica_reteica} pct={f.reteica_pct}
              onActiva={(v) => set('aplica_reteica', v)} onPct={(v) => set('reteica_pct', v)} />
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
              <input type="checkbox" checked={!!f.aplica_reteiva} onChange={(e) => set('aplica_reteiva', e.target.checked)} />
              De IVA (15% del IVA)
            </label>
          </div>
        </div>
      )}

      <div style={{ ...est.tarjeta, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,260px),1fr))', gap: '12px' }}>
        {!esFactura && (
          <div><label style={est.label}>Condiciones (salen en el PDF)</label>
            <textarea rows={4} style={{ ...est.input, resize: 'vertical' }} value={f.condiciones} onChange={(e) => set('condiciones', e.target.value)} /></div>
        )}
        <div><label style={est.label}>Observaciones (salen en el PDF)</label>
          <textarea rows={4} style={{ ...est.input, resize: 'vertical' }} value={f.notas} onChange={(e) => set('notas', e.target.value)} /></div>
      </div>

      <Totales t={t} esFactura={esFactura} items={items} />

      {error && <div style={est.error}>{error}</div>}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button style={est.botonSec} onClick={onCancelar}>Cancelar</button>
        <button style={{ ...est.boton, opacity: guardando ? 0.6 : 1 }} disabled={guardando} onClick={guardar}>
          {guardando ? 'Guardando…' : inicial ? 'Guardar cambios' : `Crear ${NOMBRE[tipo]}`}
        </button>
      </div>
    </div>
  );
}

function Retencion({ txt, activa, pct, onActiva, onPct }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
      <input type="checkbox" checked={!!activa} onChange={(e) => onActiva(e.target.checked)} />
      {txt}
      <input type="number" step="0.01" min="0" disabled={!activa} value={pct} onChange={(e) => onPct(e.target.value)}
        style={{ ...est.input, width: '76px', padding: '5px 7px', opacity: activa ? 1 : 0.4 }} />%
    </label>
  );
}

function BuscarProducto({ onEscoger }) {
  const [q, setQ] = useState('');
  const [res, setRes] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [aviso, setAviso] = useState('');
  const pedido = useRef(0);

  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); setAviso(''); return; }
    const n = ++pedido.current;
    const t = setTimeout(async () => {
      setBuscando(true);
      try {
        const { data } = await api.get('/api/caucared/productos', { params: { q } });
        if (n !== pedido.current) return;
        setRes(data); setAviso(data.length ? '' : 'No hay productos del catálogo con esas palabras.');
      } catch (e) { setAviso(mensajeDeError(e, 'No se pudo buscar')); }
      finally { if (n === pedido.current) setBuscando(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div>
      <label style={est.label}>Agregar del catálogo</label>
      <input style={est.input} placeholder="Escribe el producto: portátil hp, router tp-link, cámara…" value={q} onChange={(e) => setQ(e.target.value)} />
      {buscando && <div style={{ fontSize: '12px', color: CAU.tenue, marginTop: '6px' }}>Buscando…</div>}
      {aviso && <div style={{ fontSize: '12px', color: CAU.tenue, marginTop: '6px' }}>{aviso}</div>}
      {res.length > 0 && (
        <div style={{ marginTop: '6px', border: `1px solid ${CAU.borde}`, borderRadius: '8px', maxHeight: '280px', overflowY: 'auto' }}>
          {res.map((p) => (
            <div key={p.id} onClick={() => { onEscoger(p); setQ(''); setRes([]); }}
              style={{ padding: '9px 12px', borderBottom: `1px solid ${CAU.lineas}`, cursor: 'pointer', display: 'flex', gap: '10px', alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', overflowWrap: 'anywhere' }}>{p.nombre}</div>
                <div style={{ fontSize: '11px', color: CAU.tenue }}>{p.sku} · {p.proveedor}{p.costo == null ? ' · costo desconocido' : ''}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 700, color: CAU.acento }}>{fmt(p.precio_sugerido)}</div>
                <div style={{ fontSize: '10px', color: CAU.tenue }}>{p.origen_precio}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Renglon({ it, n, calc, responsableIva, onCambiar, onQuitar, onSubir, onBajar }) {
  const costoConocido = it.costo != null && it.costo !== '';
  const ganancia = costoConocido ? calc.subtotal - costoSinIva(it.costo, calc.total > calc.subtotal ? it.iva_pct : 0) * Number(it.cantidad || 0) : null;
  const pct = costoConocido && calc.subtotal > 0 ? Math.round(ganancia / calc.subtotal * 100) : null;
  const campo = { flex: '1 1 90px', minWidth: 0 };
  return (
    <div style={{ border: `1px solid ${CAU.lineas}`, borderRadius: '10px', padding: '10px' }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
        <div style={{ color: CAU.acento, fontWeight: 800, paddingTop: '8px', minWidth: '18px' }}>{n}</div>
        <textarea rows={2} style={{ ...est.input, resize: 'vertical', flex: 1 }} placeholder="Descripción"
          value={it.descripcion} onChange={(e) => onCambiar('descripcion', e.target.value)} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <button title="Subir" style={{ ...est.botonSec, padding: '2px 8px' }} onClick={onSubir}>↑</button>
          <button title="Bajar" style={{ ...est.botonSec, padding: '2px 8px' }} onClick={onBajar}>↓</button>
        </div>
        <button title="Quitar" style={{ ...est.botonSec, padding: '6px 10px', color: CAU.rojo }} onClick={onQuitar}>✕</button>
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px', paddingLeft: '26px' }}>
        <div style={campo}><label style={est.label}>Cantidad</label>
          <input type="number" min="0" step="any" style={est.input} value={it.cantidad} onChange={(e) => onCambiar('cantidad', e.target.value)} /></div>
        <div style={{ ...campo, flex: '2 1 130px' }}><label style={est.label}>Valor unitario {responsableIva ? '(IVA incluido)' : ''}</label>
          <input type="number" min="0" style={est.input} value={it.precio_unitario} onChange={(e) => onCambiar('precio_unitario', e.target.value)} /></div>
        <div style={campo}><label style={est.label}>Desc. %</label>
          <input type="number" min="0" max="100" style={est.input} value={it.descuento_pct} onChange={(e) => onCambiar('descuento_pct', e.target.value)} /></div>
        {responsableIva && (
          <div style={campo}><label style={est.label}>IVA</label>
            <select style={est.input} value={it.iva_pct} onChange={(e) => onCambiar('iva_pct', e.target.value)}>
              <option value={19}>19%</option><option value={5}>5%</option><option value={0}>0% / exento</option>
            </select></div>
        )}
        <div style={{ ...campo, textAlign: 'right' }}><label style={est.label}>Total renglón</label>
          <div style={{ fontWeight: 800, padding: '9px 0' }}>{fmt(calc.total)}</div></div>
      </div>
      {/* Solo en pantalla, nunca en el PDF */}
      <div style={{ fontSize: '11px', color: CAU.tenue, marginTop: '6px', paddingLeft: '26px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span title="Esto no sale en el documento">🔒</span>
        {it.catalogo_producto_id ? (
          costoConocido
            ? <span>Costo real {fmt(it.costo)} · deja <b style={{ color: ganancia < 0 ? CAU.rojo : CAU.verde }}>{fmt(ganancia)}</b>{pct != null ? ` (${pct}%)` : ''}</span>
            : <span style={{ color: CAU.acento }}>Costo desconocido: este renglón cuenta ganancia cero</span>
        ) : (
          <>
            <span>Lo que te cuesta (materiales):</span>
            <input type="number" min="0" placeholder="0" value={it.costo ?? ''} onChange={(e) => onCambiar('costo', e.target.value)}
              style={{ ...est.input, width: '120px', padding: '4px 7px', fontSize: '12px' }} />
            {ganancia != null && <span>deja {fmt(ganancia)}</span>}
          </>
        )}
        {it.origen && <span>· precio: {it.origen}</span>}
      </div>
    </div>
  );
}

function Totales({ t, esFactura, items, doc }) {
  const fila = (a, b, fuerte) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontWeight: fuerte ? 800 : 400, fontSize: fuerte ? '16px' : '14px' }}>
      <span>{a}</span><span>{b}</span>
    </div>
  );
  const ret = t.retefuente + t.reteica + t.reteiva;
  /**
   * Renglón por renglón, igual que el servidor (gananciaDocumento): un producto del catálogo
   * sin costo conocido aporta CERO, no su precio entero. Restar el costo total al subtotal
   * contaba esos renglones como ganancia pura — la pantalla decía 100% de ganancia en un
   * producto cuyo costo nadie sabe.
   */
  let gananciaVista = null, sinCosto = 0;
  if (items) {
    gananciaVista = 0;
    items.forEach((it, i) => {
      const sub = t.renglones[i]?.subtotal || 0;
      const sinDato = it.costo == null || it.costo === '';
      if (it.catalogo_producto_id && sinDato) { sinCosto++; return; }
      const conIva = (t.renglones[i]?.iva || 0) > 0;
      gananciaVista += sub - (sinDato ? 0 : costoSinIva(it.costo, conIva ? it.iva_pct : 0) * Number(it.cantidad || 0));
    });
  }
  const ganancia = doc ? Number(doc.ganancia) : gananciaVista;
  const sinCostoN = doc ? Number(doc.renglones_sin_costo) : sinCosto;
  return (
    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {ganancia != null && (
        <div style={{ ...est.tarjeta, flex: '1 1 240px', maxWidth: '360px', borderStyle: 'dashed', alignSelf: 'flex-start' }}>
          <div style={{ fontSize: '11px', color: CAU.tenue, fontWeight: 700, marginBottom: '6px' }}>🔒 SOLO LO VES TÚ</div>
          {fila('Ganancia (sin IVA)', fmt(ganancia), true)}
          {t.subtotal > 0 && <div style={{ fontSize: '12px', color: CAU.tenue }}>{Math.round(ganancia / t.subtotal * 100)}% de la venta</div>}
          {sinCostoN > 0 && <div style={{ fontSize: '12px', color: CAU.acento, marginTop: '6px' }}>
            {sinCostoN} renglón{sinCostoN > 1 ? 'es' : ''} sin costo conocido: cuenta{sinCostoN > 1 ? 'n' : ''} ganancia cero.</div>}
        </div>
      )}
      <div style={{ ...est.tarjeta, flex: '1 1 260px', maxWidth: '380px', borderColor: CAU.borde }}>
        {fila(t.iva > 0 ? 'Subtotal sin IVA' : 'Subtotal', fmt(t.subtotal))}
        {t.iva > 0 && fila('IVA', fmt(t.iva))}
        {fila('Total', fmt(t.total), true)}
        {esFactura && ret > 0 && (<>
          {t.retefuente > 0 && fila('− Ret. en la fuente', fmt(t.retefuente))}
          {t.reteica > 0 && fila('− Ret. de ICA', fmt(t.reteica))}
          {t.reteiva > 0 && fila('− Ret. de IVA', fmt(t.reteiva))}
          <div style={{ color: CAU.acento }}>{fila('Neto a recibir', fmt(t.neto), true)}</div>
        </>)}
      </div>
    </div>
  );
}

// ─── VISTA ───────────────────────────────────────────────────

function Ver({ tipo, cfg, doc, onVolver, onEditar, onCambio, onBorrado, onVerFactura }) {
  const esFactura = tipo === 'factura';
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [facturando, setFacturando] = useState(false);
  const [fp, setFp] = useState({ forma_pago: 'credito', plazo_dias: cfg.plazo_dias ?? 30 });
  const [pago, setPago] = useState(null);
  const [fe, setFe] = useState(doc.numero_fe || '');

  useEffect(() => { setFe(doc.numero_fe || ''); }, [doc]);

  const accion = async (fn) => {
    setError(''); setOcupado(true);
    try { await fn(); } catch (e) { setError(mensajeDeError(e)); } finally { setOcupado(false); }
  };

  const t = {
    subtotal: Number(doc.subtotal), iva: Number(doc.iva), total: Number(doc.total),
    retefuente: Number(doc.retefuente || 0), reteica: Number(doc.reteica || 0), reteiva: Number(doc.reteiva || 0), neto: Number(doc.neto || doc.total),
  };
  const anulada = doc.estado === 'anulada';
  const saldo = Number(doc.neto) - Number(doc.pagado);
  const puedeEditar = esFactura ? (!anulada && Number(doc.pagado) === 0) : doc.estado !== 'facturada';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <button style={est.botonSec} onClick={onVolver}>← Lista</button>
        <h2 style={{ margin: 0, fontSize: '20px', color: CAU.acento }}>{esFactura ? 'Factura' : 'Cotización'} {doc.numero}</h2>
        <Pastilla info={esFactura ? ESTADOS_PAGO[doc.estado_pago] : ESTADOS_COT[doc.estado]} />
      </div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button style={est.boton} onClick={() => imprimir(doc, cfg, tipo)}>🖨️ PDF / Imprimir</button>
        <button style={est.botonSec} onClick={() => abrirWhatsapp(doc, cfg, tipo)}>💬 WhatsApp</button>
        {puedeEditar && <button style={est.botonSec} onClick={onEditar}>✏️ Corregir</button>}
        {!esFactura && doc.estado !== 'facturada' && (<>
          {['enviada', 'aceptada', 'rechazada'].filter((e) => e !== doc.estado).map((e) => (
            <button key={e} disabled={ocupado} style={est.botonSec}
              onClick={() => accion(async () => { await api.put(`${URL.cotizacion}/${doc.id}/estado`, { estado: e }); onCambio(); })}>
              Marcar {ESTADOS_COT[e].txt.toLowerCase()}
            </button>
          ))}
          <button style={{ ...est.botonSec, borderColor: CAU.borde, color: CAU.acento }} onClick={() => setFacturando(true)}>🧾 Facturar</button>
          <button style={{ ...est.botonSec, color: CAU.rojo }} disabled={ocupado}
            onClick={() => window.confirm(`¿Borrar la cotización ${doc.numero}? No se puede deshacer.`) &&
              accion(async () => { await api.delete(`${URL.cotizacion}/${doc.id}`); onBorrado(); })}>Borrar</button>
        </>)}
        {!esFactura && doc.estado === 'facturada' && doc.factura_id && (
          <button style={est.botonSec} onClick={() => onVerFactura(doc.factura_id)}>Ver la factura →</button>
        )}
        {esFactura && !anulada && saldo > 0 && (
          <button style={{ ...est.botonSec, borderColor: CAU.borde, color: CAU.verde }}
            onClick={() => setPago({ monto: saldo, fecha: hoyISO(), medio: 'transferencia', referencia: '' })}>💵 Registrar pago</button>
        )}
        {esFactura && !anulada && (
          <button style={{ ...est.botonSec, color: CAU.rojo }} disabled={ocupado}
            onClick={() => {
              const motivo = window.prompt(`¿Por qué se anula la factura ${doc.numero}? (el número queda gastado)`);
              if (motivo) accion(async () => { await api.post(`${URL.factura}/${doc.id}/anular`, { motivo }); onCambio(); });
            }}>Anular</button>
        )}
      </div>

      {error && <div style={est.error}>{error}</div>}

      {facturando && (
        <div style={{ ...est.tarjeta, borderColor: CAU.borde }}>
          <div style={{ fontWeight: 700, marginBottom: '10px' }}>Convertir en factura</div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div><label style={est.label}>Forma de pago</label>
              <select style={est.input} value={fp.forma_pago} onChange={(e) => setFp({ ...fp, forma_pago: e.target.value })}>
                <option value="credito">Crédito</option><option value="contado">Contado</option></select></div>
            {fp.forma_pago === 'credito' && <div><label style={est.label}>Plazo (días)</label>
              <input type="number" style={{ ...est.input, width: '100px' }} value={fp.plazo_dias} onChange={(e) => setFp({ ...fp, plazo_dias: e.target.value })} /></div>}
            <button style={est.boton} disabled={ocupado} onClick={() => accion(async () => {
              const { data } = await api.post(`${URL.cotizacion}/${doc.id}/facturar`, { ...fp, fecha: hoyISO() });
              setFacturando(false); onVerFactura(data.id);
            })}>Crear factura</button>
            <button style={est.botonSec} onClick={() => setFacturando(false)}>Cancelar</button>
          </div>
          <div style={{ fontSize: '12px', color: CAU.tenue, marginTop: '8px' }}>
            Las retenciones salen de la ficha del cliente y los costos se releen del catálogo de hoy. Después se puede corregir mientras no tenga pagos.
          </div>
        </div>
      )}

      {pago && (
        <div style={{ ...est.tarjeta, borderColor: 'rgba(52,211,153,0.35)' }}>
          <div style={{ fontWeight: 700, marginBottom: '10px' }}>Registrar pago · saldo {fmt(saldo)}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,150px),1fr))', gap: '10px' }}>
            <div><label style={est.label}>Valor</label>
              <input type="number" style={est.input} value={pago.monto} onChange={(e) => setPago({ ...pago, monto: e.target.value })} /></div>
            <div><label style={est.label}>Fecha</label>
              <input type="date" style={est.input} value={pago.fecha} onChange={(e) => setPago({ ...pago, fecha: e.target.value })} /></div>
            <div><label style={est.label}>Medio</label>
              <select style={est.input} value={pago.medio} onChange={(e) => setPago({ ...pago, medio: e.target.value })}>
                <option value="transferencia">Transferencia</option><option value="consignacion">Consignación</option>
                <option value="efectivo">Efectivo</option><option value="cheque">Cheque</option><option value="otro">Otro</option>
              </select></div>
            <div><label style={est.label}>Referencia</label>
              <input style={est.input} value={pago.referencia} onChange={(e) => setPago({ ...pago, referencia: e.target.value })} /></div>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
            <button style={est.boton} disabled={ocupado} onClick={() => accion(async () => {
              const { data } = await api.post(`${URL.factura}/${doc.id}/pagos`, pago);
              setPago(null); onCambio(data);
            })}>Guardar pago</button>
            <button style={est.botonSec} onClick={() => setPago(null)}>Cancelar</button>
          </div>
          <div style={{ fontSize: '12px', color: CAU.tenue, marginTop: '8px' }}>Este pago queda solo en CAUCARED: no entra a la caja ni al Dinero de LinkUs.</div>
        </div>
      )}

      {anulada && (
        <div style={est.error}>Anulada el {fecha(doc.anulada_en)}. Motivo: {doc.motivo_anulacion}</div>
      )}

      <div style={{ ...est.tarjeta, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,220px),1fr))', gap: '12px' }}>
        <div>
          <div style={est.label}>{esFactura ? 'Facturado a' : 'Cotizado a'}</div>
          <div style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{doc.cliente_nombre}</div>
          <div style={{ fontSize: '12px', color: CAU.tenue }}>{[doc.cliente_documento, doc.cliente_contacto, doc.cliente_telefono, doc.cliente_email].filter(Boolean).join(' · ')}</div>
        </div>
        <div>
          <div style={est.label}>Fechas</div>
          <div style={{ fontSize: '13px' }}>Emitida {fecha(doc.fecha)}</div>
          <div style={{ fontSize: '13px' }}>{esFactura
            ? (doc.forma_pago === 'contado' ? 'Contado' : `Vence ${fecha(doc.fecha_vencimiento)}`)
            : `Válida hasta ${fecha(sumarDias(String(doc.fecha).slice(0, 10), doc.validez_dias))}`}</div>
        </div>
        {esFactura && (
          <div>
            <div style={est.label}>Factura electrónica</div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input style={est.input} value={fe} placeholder="N.º que dio la DIAN" onChange={(e) => setFe(e.target.value)} />
              {fe !== (doc.numero_fe || '') && (
                <button style={est.botonSec} disabled={ocupado} onClick={() => accion(async () => {
                  await api.put(`${URL.factura}/${doc.id}/fe`, { numero_fe: fe }); onCambio();
                })}>Guardar</button>
              )}
            </div>
          </div>
        )}
      </div>

      <div style={{ ...est.tarjeta, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '520px' }}>
          <thead><tr>
            <th style={est.th}>#</th><th style={est.th}>Descripción</th><th style={{ ...est.th, textAlign: 'center' }}>Cant.</th>
            <th style={{ ...est.th, textAlign: 'right' }}>Valor unit.</th><th style={{ ...est.th, textAlign: 'center' }}>IVA</th>
            <th style={{ ...est.th, textAlign: 'right' }}>Total</th>
          </tr></thead>
          <tbody>
            {doc.items.map((it, i) => (
              <tr key={it.id}>
                <td style={est.td}>{i + 1}</td>
                <td style={{ ...est.td, whiteSpace: 'pre-wrap' }}>{it.descripcion}
                  {Number(it.descuento_pct) > 0 && <span style={{ color: CAU.tenue }}> · desc. {Number(it.descuento_pct)}%</span>}</td>
                <td style={{ ...est.td, textAlign: 'center' }}>{Number(it.cantidad)}</td>
                <td style={{ ...est.td, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(it.precio_unitario)}</td>
                <td style={{ ...est.td, textAlign: 'center' }}>{Number(it.iva_pct)}%</td>
                <td style={{ ...est.td, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt(Number(it.subtotal) + Number(it.iva))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Totales t={t} esFactura={esFactura} doc={doc} />

      {esFactura && (doc.pagos || []).length > 0 && (
        <div style={est.tarjeta}>
          <div style={{ fontWeight: 700, marginBottom: '8px' }}>Pagos · recibido {fmt(doc.pagado)} · saldo {fmt(Math.max(saldo, 0))}</div>
          {doc.pagos.map((p) => (
            <div key={p.id} style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${CAU.lineas}`, flexWrap: 'wrap', opacity: p.anulado ? 0.5 : 1 }}>
              <div style={{ minWidth: '84px' }}>{fecha(p.fecha)}</div>
              <div style={{ flex: 1, fontSize: '13px' }}>{p.medio}{p.referencia ? ` · ${p.referencia}` : ''}
                {p.anulado && <span style={{ color: CAU.rojo }}> · anulado: {p.motivo_anulacion}</span>}</div>
              <div style={{ fontWeight: 700, textDecoration: p.anulado ? 'line-through' : 'none' }}>{fmt(p.monto)}</div>
              {!p.anulado && (
                <button style={{ ...est.botonSec, padding: '4px 10px', fontSize: '12px', color: CAU.rojo }} disabled={ocupado}
                  onClick={() => {
                    const motivo = window.prompt('¿Por qué se anula este pago?');
                    if (motivo) accion(async () => { const { data } = await api.post(`/api/caucared/pagos/${p.id}/anular`, { motivo }); onCambio(data); });
                  }}>Anular</button>
              )}
            </div>
          ))}
        </div>
      )}

      {(doc.condiciones || doc.notas) && (
        <div style={{ ...est.tarjeta, fontSize: '13px', whiteSpace: 'pre-wrap' }}>
          {doc.condiciones && <><div style={est.label}>Condiciones</div><div style={{ marginBottom: '8px' }}>{doc.condiciones}</div></>}
          {doc.notas && <><div style={est.label}>Observaciones</div><div>{doc.notas}</div></>}
        </div>
      )}
    </div>
  );
}
